import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import classnames from "classnames";
import { getCurrentCulture } from "@utils";
import { useCreate24hLock } from "@state/hooks/header/useCreate24hLock";

import { Button } from "@components/buttons";
import LockInstructionsPopup from "./LockInstructionsPopup";
import { useRootAppStore } from "@hooks";

export default function HeaderLockButton() {
	const { t } = useTranslation();

	const holdRef = useRef<any | null>(null);

	const rootStore = useRootAppStore();

	const { create24hLock } = useCreate24hLock();

	const [isInstructionPopupVisible, setIsInstructionsPopupVisible] =
		useState(false);

	const [position, setPosition] = useState<number | null>(null);
	const [isHolding, setIsHolding] = useState(false);
	const [fillPercent, setFillPercent] = useState(0);
	const fillIntervalRef = useRef<NodeJS.Timeout | null>(null);

	useEffect(() => {
		const handleTouchMove = (e: TouchEvent) => {
			if (!isHolding || position === null) return;

			const currentY = e.touches[0].clientY;
			const deltaY = Math.abs(currentY - position);

			if (deltaY > 5) {
				setIsHolding(false);
				setPosition(null);
				stopClickTimer();
				setFillPercent(0);
				return;
			}

			setPosition(currentY);
		};

		const el = holdRef.current;
		if (el) {
			el.addEventListener("touchmove", handleTouchMove, {
				passive: true,
			});
		}

		return () => {
			if (el) {
				el.removeEventListener("touchmove", handleTouchMove);
			}
		};
	}, [isHolding, position]);

	const startClickTimer = () => {
		if (fillIntervalRef.current) return;

		fillIntervalRef.current = setInterval(() => {
			setFillPercent((prev) => {
				if (prev >= 100) {
					clearInterval(fillIntervalRef.current!);
					create24hLock();
					return 100;
				}

				return prev + 1;
			});
		}, 50);
	};

	const stopClickTimer = () => {
		if (fillIntervalRef.current) {
			clearInterval(fillIntervalRef.current);
			fillIntervalRef.current = null;
		}

		if (fillPercent >= 99) {
			clearInterval(fillIntervalRef.current!);
			create24hLock();
			return;
		}

		if (rootStore.user24hLockStore.isActive) {
			setFillPercent(0);
			return;
		}

		fillIntervalRef.current = setInterval(() => {
			setFillPercent((prev) => {
				if (prev <= 0) {
					clearInterval(fillIntervalRef.current!);
					fillIntervalRef.current = null;
					return 0;
				}
				return prev - 5;
			});
		}, 50);
	};

	useEffect(() => {
		return () => {
			if (fillIntervalRef.current) {
				clearInterval(fillIntervalRef.current);
				fillIntervalRef.current = null;
			}
		};
	}, []);

	return (
		<>
			<button
				ref={holdRef}
				className="header__lock btn--reset"
				onTouchStart={(e) => {
					setIsHolding(true);
					setPosition(e.touches?.[0]?.clientY ?? null);
					startClickTimer();
				}}
				onTouchEnd={() => {
					setIsHolding(false);
					setPosition(null);
					stopClickTimer();
				}}
			>
				<div
					className="header__lock__fill"
					style={{
						width: `${fillPercent}%`,
					}}
				/>
				<Button
					className="header__lock__icon btn--square btn--square--reset"
					iconOnly={true}
					iconClassName="u-icon--sml u-icon--lock u-color--core--snow"
					onClick={() => setIsInstructionsPopupVisible(true)}
				/>
				<Button
					className={classnames("header__lock__handle btn--reset", {
						"is-long":
							getCurrentCulture() == "da" ||
							getCurrentCulture() == "es" ||
							getCurrentCulture() == "fr" ||
							getCurrentCulture() == "it" ||
							getCurrentCulture() == "sq" ||
							getCurrentCulture() == "sv",
					})}
					btnTextClassName={classnames("header__lock__label", {
						"is-long":
							getCurrentCulture() == "da" ||
							getCurrentCulture() == "es" ||
							getCurrentCulture() == "fr" ||
							getCurrentCulture() == "it" ||
							getCurrentCulture() == "sq" ||
							getCurrentCulture() == "sv",
					})}
					btnText={t<string>("HEADER.LOCK_BUTTON_LABEL")}
				/>
			</button>

			<LockInstructionsPopup
				isInstructionPopupVisible={isInstructionPopupVisible}
				onClose={() => setIsInstructionsPopupVisible(false)}
			/>
		</>
	);
}
